import React from "react";
import {
  Globe,
  Scale,
  Palette,
  Layout,
  Package,
  CreditCard,
  Boxes,
  Megaphone,
  TrendingUp,
  Headphones,
  BookOpen,
  Users,
  Briefcase,
  BarChart3,
  Bot,
  Shield,
  Crown,
} from "lucide-react";

/* =========================================================
   SUPERAPP — UI CATEGORIES
   Single source of truth for everything the marketing side of
   the app says and shows. CentralHome imports from here and
   renders it; nothing below is hardcoded in a component.

   Lives at:  src/SUPERAPP/CATEGORIES/UICategories.tsx
   (same folder as AppCategories.tsx — that file owns the
   signed-in hub's category tree, this one owns the landing
   page's content.)

   Each entry sits on ONE line where it fits, so you can
   comment/uncomment with // to turn things on and off.
========================================================= */

/* =========================================================
   1️⃣ BRAND TOKENS — red / black, matching AppCategories.
========================================================= */

export const M = {
  blue: "#0A0A0A",
  blueLit: "#1F1F1F",
  blueDeep: "#000000",
  blueInk: "#0A0A0A",
  night: "#050505",

  red: "#DC2626",
  redDeep: "#9F1414",

  yellow: "#F5B301",
  yellowDeep: "#C99200",

  chrome: "#EDEDED",
  chromeDim: "#B5B5B5",
  pearl: "#FFFFFF",

  mist: "#F7F7F7",
  slate: "#5A5A5A",
  faint: "#9A9A9A",

  live: "#16A34A",
  liveDeep: "#0F7A38",
};

/* the bevel, in three weights */
export const BEVEL_SM = `0 0 0 2px ${M.pearl}, 0 0 0 4px ${M.blue}`;
export const BEVEL = `0 0 0 3px ${M.pearl}, 0 0 0 6px ${M.blue}, 0 6px 0 ${M.blueDeep}`;
export const BEVEL_LG = `0 0 0 5px ${M.pearl}, 0 0 0 11px ${M.blue}, 0 16px 0 ${M.blueDeep}, 0 46px 70px -30px rgba(4,8,32,.75)`;

/* the wordmark sweep, left to right */
export const SWEEP = `linear-gradient(90deg, ${M.blue}, ${M.chrome}, ${M.red}, ${M.yellow}, ${M.chrome}, ${M.blueLit}, ${M.blue})`;

/* =========================================================
   2️⃣ HERO — the clip, the headline, the proof line.
========================================================= */

export interface HeroFace {
  letter: string;
  color: string;
}

export const HERO_CLIP = "";

export const Hero = {
  eyebrow: "Business management · One dashboard",
  lead: "Run the",
  highlight: "whole",
  tail: "business",
  close: "from here.",
  lede: "Domain, brand, website, products, payments, inventory, marketing, sales, support, books, team, analytics, and an AI CEO watching over it all — in one place.",
  primaryCta: "Start your business",
  secondaryCta: "See what's inside",
  faces: [
    { letter: "D", color: M.yellow },
    { letter: "B", color: M.red },
    { letter: "S", color: M.chrome },
    { letter: "+", color: M.blueLit },
  ] as HeroFace[],
  proofAccounts: "17 modules",
  proofRating: "1 dashboard",
  standCaption: "Every module, one login",
};

/* =========================================================
   3️⃣ NAV — header links, mobile menu.
========================================================= */

export interface NavLinkItem {
  id: number;
  label: string;
  href: string;
}

export const NavLinks: NavLinkItem[] = [
  { id: 1, label: "What you get", href: "#what" },
  { id: 2, label: "How it works", href: "#start" },
  { id: 3, label: "Questions", href: "#asked" },
];

/* =========================================================
   4️⃣ RAIL — the yellow line of claims, running forever.
========================================================= */

export const RailClaims: string[] = [
  "Domain, brand and website in one flow",
  "Products sync everywhere automatically",
  "Payments, invoices and payouts included",
  "Inventory updates itself as orders land",
  "Marketing, sales and support in one inbox",
  "An AI CEO watching every module",
];

/* =========================================================
   5️⃣ STATS — the four counters under the rail.
========================================================= */

export interface StatItem {
  id: number;
  label: string;
  to?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  display?: string;
}

export const Stats: StatItem[] = [
  { id: 1, label: "Modules included", to: 17 },
  { id: 2, label: "Setup time", display: "~10 min" },
  { id: 3, label: "Separate tools needed", display: "0" },
  { id: 4, label: "Places to check each morning", display: "1" },
];

/* =========================================================
   6️⃣ SECTION HEADS — eyebrow, two-line title, side note.
========================================================= */

export interface SectionHead {
  id: string;
  eyebrow: string;
  titleTop: string;
  titleBottom: string;
  aside?: string;
}

export const SectionHeads: Record<string, SectionHead> = {
  what: { id: "what", eyebrow: "What you get", titleTop: "Seventeen modules,", titleBottom: "one business.", aside: "No separate site builder, no separate invoicing tool, no separate support inbox. It's all under one roof." },
  start: { id: "start", eyebrow: "Getting started", titleTop: "Three steps,", titleBottom: "in this order.", aside: "Registering your business is the part that takes real time — everything else is quick after that." },
  said: { id: "said", eyebrow: "From people running their business here", titleTop: "What people", titleBottom: "actually said." },
  asked: { id: "asked", eyebrow: "Questions", titleTop: "The ones", titleBottom: "we get most." },
};

/* =========================================================
   7️⃣ TILES — the bento grid, one per module family.
   `span` is the grid width (wide / half / third) and `tone`
   is the surface: "" translucent panel / "money" yellow /
   "plate" white plate.
========================================================= */

export type TileSpan = "wide" | "half" | "third";
export type TileTone = "" | "money" | "plate";

export interface TileItem {
  id: number;
  span: TileSpan;
  tone: TileTone;
  icon: React.ReactNode;
  title: string;
  body: string;
}

export const Tiles: TileItem[] = [
  { id: 1, span: "wide", tone: "money", icon: <Layout size={22} strokeWidth={2.8} />, title: "Domain, brand and website, together", body: "Register or connect a domain, generate a logo and style guide, then build the site on top of it — all three stay linked, so a brand update carries through automatically." },
  { id: 2, span: "third", tone: "", icon: <Scale size={22} strokeWidth={2.6} />, title: "Legal, handled", body: "Business registration, terms, privacy policy and basic contracts generated for your business type and country, kept current as rules change." },
  { id: 3, span: "third", tone: "", icon: <Package size={22} strokeWidth={2.6} />, title: "Products in one place", body: "Add pricing, variants and descriptions once. They sync across your storefront, invoices and any sales channels you connect." },
  { id: 4, span: "third", tone: "plate", icon: <CreditCard size={22} strokeWidth={2.6} />, title: "Payments built in", body: "Accept cards, bank transfers and wallets at checkout or by link, with payouts straight to your own bank account." },
  { id: 5, span: "half", tone: "", icon: <Boxes size={22} strokeWidth={2.6} />, title: "Inventory that tracks itself", body: "Stock levels update as orders come in, low-stock alerts fire on their own, and shipping labels generate straight from an order." },
  { id: 6, span: "half", tone: "", icon: <Bot size={22} strokeWidth={2.6} />, title: "An AI CEO overseeing it all", body: "A daily overview pulls signals from every module — sales, cash flow, support backlog — and surfaces what needs your attention first." },
];

/* =========================================================
   8️⃣ MODULE STRIP — the seventeen sections, shown as chips
   on the landing page. Sourced from appCategories elsewhere;
   this array is just the display copy for each.
========================================================= */

export interface ModuleBlurb {
  id: number;
  name: string;
  icon: React.ReactNode;
  body: string;
}

export const ModuleBlurbs: ModuleBlurb[] = [
  { id: 1,  name: "Domain",               icon: <Globe size={18} />,      body: "Buy, connect, or start on a free subdomain." },
  { id: 2,  name: "Legal Business",       icon: <Scale size={18} />,      body: "Registration, T&Cs, and contracts, generated for you." },
  { id: 3,  name: "Brand",                icon: <Palette size={18} />,    body: "Logo, palette, fonts and a style guide, all linked." },
  { id: 4,  name: "Website",              icon: <Layout size={18} />,     body: "Visual builder, no code required." },
  { id: 5,  name: "Products",             icon: <Package size={18} />,    body: "One catalogue, synced everywhere." },
  { id: 6,  name: "Payments",             icon: <CreditCard size={18} />, body: "Checkout, links, and payouts to your bank." },
  { id: 7,  name: "Inventory & Fulfilment", icon: <Boxes size={18} />,    body: "Stock and shipping, updated automatically." },
  { id: 8,  name: "Marketing",            icon: <Megaphone size={18} />, body: "Email, discounts, referrals, and social scheduling." },
  { id: 9,  name: "Sales",                icon: <TrendingUp size={18} />, body: "A simple pipeline from lead to paid." },
  { id: 10, name: "Customer Service",     icon: <Headphones size={18} />, body: "One shared inbox for every channel." },
  { id: 11, name: "Bookkeeping",          icon: <BookOpen size={18} />,   body: "Transactions categorised, reports on demand." },
  { id: 12, name: "Employees",            icon: <Users size={18} />,      body: "Roles, hours, and pay from one dashboard." },
  { id: 13, name: "Business Management",  icon: <Briefcase size={18} />,  body: "Tasks, documents, calendars, day to day." },
  { id: 14, name: "Analytics",            icon: <BarChart3 size={18} />,  body: "Revenue, traffic, and behaviour, in one view." },
  { id: 15, name: "AI & Automation",      icon: <Bot size={18} />,        body: "Repetitive work handled, you approve the rest." },
  { id: 16, name: "Security",             icon: <Shield size={18} />,     body: "Encryption, roles, two-factor, and activity logs." },
  { id: 17, name: "AI CEO System",        icon: <Crown size={18} />,      body: "One daily overview across every module." },
];

/* =========================================================
   9️⃣ STEPS — getting a business running, in order.
========================================================= */

export interface StepItem {
  id: number;
  n: string;
  title: string;
  body: string;
}

export const Steps: StepItem[] = [
  { id: 1, n: "01", title: "Sign up and name your business", body: "Email, a password, and your business name. Nothing else at this stage." },
  { id: 2, n: "02", title: "Set up domain, brand and legal", body: "Connect or register a domain, generate a brand kit, and get your business registration and core documents in place." },
  { id: 3, n: "03", title: "Add products and go live", body: "Add what you sell, connect payments, and publish your site — every module is ready to use from day one." },
];

/* =========================================================
   🔟 SAID — reviews, rendered as chat bubbles.
========================================================= */

export interface SaidItem {
  id: number;
  who: string;
  text: string;
  alt: boolean;
}

export const Said: SaidItem[] = [
  { id: 1, who: "Amara, founder", text: "I had a domain, a logo, and a working storefront before lunch. Didn't touch a single other tool.", alt: false },
  { id: 2, who: "Femi, small business owner", text: "The AI CEO overview each morning is the closest thing I've had to an assistant. It just tells me what actually needs me.", alt: true },
  { id: 3, who: "Chidinma, ops lead", text: "Inventory and support used to live in two different apps that never agreed with each other. Now they're the same screen.", alt: false },
];

/* =========================================================
   1️⃣1️⃣ SECURITY — the protection panel and its badges.
========================================================= */

export interface SecurityBadge {
  id: number;
  label: string;
  icon: React.ReactNode;
}

export const Security = {
  title: "Built to keep your business's data secure",
  body: "Data is encrypted in storage, team access is role-based, and every login supports two-factor authentication. Activity logs show exactly who changed what, and when.",
  badges: [
    { id: 1, label: "Encrypted storage", icon: <Shield size={13} strokeWidth={2.8} /> },
    { id: 2, label: "Role-based team access", icon: <Users size={13} strokeWidth={2.8} /> },
    { id: 3, label: "Two-factor login", icon: <Crown size={13} strokeWidth={2.8} /> },
  ] as SecurityBadge[],
};

/* =========================================================
   1️⃣2️⃣ ASKED — the FAQ accordion, one per top-level module.
   Kept in sync with the module-by-module FAQ set.
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export interface AskedItem {
  id: number;
  q: string;
  a: string;
  color?: string;
}

export const Asked: AskedItem[] = [
  { id: 1,  q: "Do I need my own domain, or can I get one through the platform?", a: "Buy a new domain, connect one you already own, or start on a free subdomain and upgrade later — DNS and renewals are handled either way.", color: M.chrome },
  { id: 2,  q: "What does 'Legal Business' actually set up for me?", a: "Business registration, terms & conditions, privacy policy and basic contracts, generated for your business type and country and kept up to date as rules change.", color: M.red },
  { id: 3,  q: "Can I build a full brand identity, not just a logo?", a: "Logo, color palette, fonts and a style guide are generated together, and stay linked so your site, socials and invoices all match automatically.", color: M.chrome },
  { id: 4,  q: "Do I need to know how to code to build the website?", a: "No. Pick a template, edit it visually, and publish — custom code is optional for anyone who wants it.", color: M.red },
  { id: 5,  q: "How do I add and manage products?", a: "Add products or services with pricing, variants and descriptions once, and they sync across your storefront, invoices and any sales channels you connect.", color: M.chrome },
  { id: 6,  q: "How do payments work?", a: "Accept cards, bank transfers and popular wallets on checkout or via a payment link, with payouts to your own bank account — no separate payment provider needed.", color: M.red },
  { id: 7,  q: "Does it handle stock and shipping?", a: "Stock levels update automatically as orders come in, low-stock alerts are built in, and you can connect shipping carriers to generate labels straight from an order.", color: M.chrome },
  { id: 8,  q: "What marketing tools are included?", a: "Email campaigns, discount codes, referral links and social post scheduling, with basic performance tracking on each so you can see what's actually working.", color: M.red },
  { id: 9,  q: "How does it help me close sales?", a: "A simple pipeline tracks leads and orders from first contact to paid, with reminders so nothing sits untouched for weeks.", color: M.chrome },
  { id: 10, q: "What's included for customer service?", a: "A shared inbox for email, chat and social messages in one place, plus canned replies and a lightweight ticket view so nothing gets missed.", color: M.red },
  { id: 11, q: "Do I still need separate accounting software?", a: "Bookkeeping is built in — transactions are categorised automatically, and you can export reports or sync to accounting software if you already use one.", color: M.chrome },
  { id: 12, q: "Can I manage employees or contractors?", a: "Add team members with role-based access, track hours or tasks, and pay salaries or invoices from the same dashboard.", color: M.red },
  { id: 13, q: "What does 'Business Management' cover that the other sections don't?", a: "It's the operations layer — tasks, documents, calendars and internal notes — for the day-to-day running of the business that doesn't fit neatly under sales or marketing.", color: M.chrome },
  { id: 14, q: "What analytics do I get?", a: "Revenue, traffic, and customer behaviour in one dashboard, with reports you can export or schedule to land in your inbox.", color: M.red },
  { id: 15, q: "What does the AI & Automation section actually do?", a: "Automates repetitive work — replying to common support questions, categorising expenses, writing product descriptions — and you approve or edit before anything goes live.", color: M.chrome },
  { id: 16, q: "How is my business's data kept secure?", a: "Encrypted storage, role-based access for your team, and two-factor login, with activity logs so you can see who changed what.", color: M.red },
  { id: 17, q: "What is the AI CEO System?", a: "A daily overview that pulls signals from every section — sales, cash flow, support backlog — and surfaces what needs your attention first, instead of you checking each module separately.", color: M.chrome },
];

/* =========================================================
   1️⃣3️⃣ CLOSING — the last panel before the footer.
========================================================= */

export const Closing = {
  eyebrow: "Takes about ten minutes to set up",
  title: "Start running your business here.",
  body: "Bring your domain, brand, website, products and team into one dashboard, with an AI CEO keeping watch across all of it.",
  primaryCta: "Start your business",
  secondaryCta: "I already have an account",
};

/* =========================================================
   1️⃣4️⃣ HUB — copy for the signed-in landing panel.
   `greeting` takes the first name; `{name}` is replaced.
========================================================= */

export const Hub = {
  eyebrow: "Your business",
  greeting: "Hey {name}.",
  fallbackName: "there",
  body: "Every module lives below. Pick one to jump in, or check today's AI CEO overview first.",
  walletCta: "Open AI CEO overview",
  profileCta: "Your profile",
  quickHeading: "Jump back in",
  sidebarHeading: "Everything",
};

/* =========================================================
   1️⃣5️⃣ DOCK — the sticky mobile bar on the landing page.
========================================================= */

export const Dock = {
  label: "Free to start",
  value: "~10 minute setup",
  cta: "Get started",
};

/* =========================================================
   1️⃣6️⃣ UICategories — single grouped export
========================================================= */

export const UICategories = {
  M,
  BEVEL_SM,
  BEVEL,
  BEVEL_LG,
  SWEEP,
  HERO_CLIP,
  Hero,
  NavLinks,
  RailClaims,
  Stats,
  SectionHeads,
  Tiles,
  ModuleBlurbs,
  Steps,
  Said,
  Security,
  Asked,
  Closing,
  Hub,
  Dock,
};

export default UICategories;